import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IntegrationProfileApiError,
  type IntegrationProfileApiRow,
  type IntegrationProfileListQuery,
  type IntegrationProfileListResponse,
  listIntegrationProfiles,
} from '@/api/integration-profiles';
import type { PaginationMeta } from '@/components/data-table';

const DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 10,
  SORT_BY: 'profile_name',
  ORDER: 'asc' as const,
} as const;

interface UseIntegrationProfilesReturn {
  profiles: IntegrationProfileApiRow[];
  pagination: PaginationMeta | undefined;
  isLoading: boolean;
  error: string | null;
  filters: IntegrationProfileListQuery;
  setSearch: (v: string) => void;
  setPage: (v: number) => void;
  setSort: (sortBy: string, order: 'asc' | 'desc') => void;
  refetch: () => void;
}

export const useIntegrationProfiles = (partyId: string): UseIntegrationProfilesReturn => {
  const [data, setData] = useState<IntegrationProfileListResponse | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<IntegrationProfileListQuery>({
    page: DEFAULTS.PAGE,
    pageSize: DEFAULTS.PAGE_SIZE,
    sortBy: DEFAULTS.SORT_BY,
    order: DEFAULTS.ORDER,
  });

  const fetchData = useCallback(async () => {
    if (!partyId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const result = await listIntegrationProfiles(partyId, filters);
      setData(result.data);
    } catch (e: unknown) {
      if (e instanceof IntegrationProfileApiError && e.status === 404) {
        setData(undefined);
        setError('Provider not found');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load integration profiles');
        setData(undefined);
      }
    } finally {
      setIsLoading(false);
    }
  }, [partyId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: DEFAULTS.PAGE }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setSort = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sortBy, order, page: DEFAULTS.PAGE }));
  }, []);

  const profiles = useMemo(() => data?.data ?? [], [data]);
  const pagination = useMemo(
    (): PaginationMeta | undefined =>
      data
        ? {
            page: data.pagination.page,
            pageSize: data.pagination.pageSize,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages,
          }
        : undefined,
    [data]
  );

  return {
    profiles,
    pagination,
    isLoading,
    error,
    filters,
    setSearch,
    setPage,
    setSort,
    refetch: fetchData,
  };
};
